import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import type { GuideUpdate } from "./useGuideUpdates";
import { pickWorkspaceGuia } from "@/components/knowledge-assistant/workspace-guia";

let channelCounter = 0;

export function useGuideUpdatesRealtime(
  onChange: () => void,
  organizationId?: string | null,
) {
  const latest = useRef(onChange);
  latest.current = onChange;

  useEffect(() => {
    if (!organizationId) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const channel = supabase
      .channel(`guide-updates-${organizationId}-${++channelCounter}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "knowledge_assistant_updates",
          filter: `organization_id=eq.${organizationId}`,
        },
        () => {
          clearTimeout(timer);
          timer = setTimeout(() => latest.current(), 400);
        },
      )
      .subscribe();

    return () => {
      clearTimeout(timer);
      supabase.removeChannel(channel);
    };
  }, [organizationId]);
}

export interface UnreadGuideUpdate {
  id: string;
  title: string;
  description: string | null;
  priority: string;
  image_url: string | null;
  assistant_id: string;
  assistant_name: string;
  created_at: string;
}

export function useGuideUpdatesUnread() {
  const [unread, setUnread] = useState<UnreadGuideUpdate[]>([]);
  const [loaded, setLoaded] = useState(false);
  const { activeWorkspaceId: organizationId, user } = useAuth();
  const userId = user?.id ?? null;

  const refresh = useCallback(async () => {
    if (!organizationId || !userId) {
      setUnread([]);
      setLoaded(true);
      return;
    }

    const nowIso = new Date().toISOString();

    const { data: assistants } = await supabase
      .rpc("list_my_knowledge_assistants");
    const workspaceGuia = pickWorkspaceGuia(
      assistants as Array<{ id: string; name: string; scope: string; organization_id: string | null }> | null,
      organizationId,
    );
    const campaignAssistants = workspaceGuia ? [workspaceGuia] : [];

    if (campaignAssistants.length === 0) {
      setUnread([]);
      setLoaded(true);
      return;
    }

    const assistantIds = campaignAssistants.map((a) => a.id);
    const assistantNames: Record<string, string> = {};
    campaignAssistants.forEach((a) => {
      assistantNames[a.id] = a.name;
    });

    const { data: updates, error } = await supabase
      .from("knowledge_assistant_updates")
      .select("id, title, description, priority, image_url, assistant_id, created_at")
      .in("assistant_id", assistantIds)
      .eq("is_active", true)
      .or(`starts_at.is.null,starts_at.lte.${nowIso}`)
      .or(`ends_at.is.null,ends_at.gte.${nowIso}`)
      .order("created_at", { ascending: false });

    if (error || !updates?.length) {
      setUnread([]);
      setLoaded(true);
      return;
    }

    const updateIds = (updates as any[]).map((u) => u.id);
    const { data: reads } = await supabase
      .from("knowledge_assistant_update_reads")
      .select("update_id")
      .eq("user_id", userId)
      .in("update_id", updateIds);

    const readSet = new Set(((reads as any[]) ?? []).map((r) => r.update_id));
    const unreadItems = (updates as any[])
      .filter((u) => !readSet.has(u.id))
      .map((u) => ({
        ...u,
        assistant_name: assistantNames[u.assistant_id] ?? "",
      }));

    setUnread(unreadItems);
    setLoaded(true);
  }, [organizationId, userId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useGuideUpdatesRealtime(refresh, organizationId);

  const markAsRead = useCallback(
    async (updateId: string) => {
      if (!userId) return;
      await supabase
        .from("knowledge_assistant_update_reads")
        .upsert({ user_id: userId, update_id: updateId } as any, {
          onConflict: "user_id,update_id",
        });
      setUnread((prev) => prev.filter((u) => u.id !== updateId));
    },
    [userId],
  );

  const markAllAsRead = useCallback(
    async (assistantId?: string) => {
      if (!userId) return;
      const toMark = assistantId
        ? unread.filter((u) => u.assistant_id === assistantId)
        : unread;
      if (toMark.length === 0) return;

      await supabase
        .from("knowledge_assistant_update_reads")
        .upsert(
          toMark.map((u) => ({ user_id: userId, update_id: u.id })) as any[],
          { onConflict: "user_id,update_id" },
        );
      const markedIds = new Set(toMark.map((u) => u.id));
      setUnread((prev) => prev.filter((u) => !markedIds.has(u.id)));
    },
    [userId, unread],
  );

  return { unread, count: unread.length, loaded, markAsRead, markAllAsRead, refetch: refresh };
}

export function useGuideUpdatesForAssistant(assistantId: string | null) {
  const { activeWorkspaceId: organizationId } = useAuth();
  const [updates, setUpdates] = useState<GuideUpdate[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!organizationId) {
      setUpdates([]);
      setLoading(false);
      return;
    }
    const nowIso = new Date().toISOString();
    let query = supabase
      .from("knowledge_assistant_updates")
      .select("*")
      .eq("is_active", true)
      .or(`starts_at.is.null,starts_at.lte.${nowIso}`)
      .or(`ends_at.is.null,ends_at.gte.${nowIso}`)
      .order("created_at", { ascending: false });

    if (assistantId) {
      query = query.eq("assistant_id", assistantId);
    } else {
      query = query.eq("organization_id", organizationId);
    }

    const { data, error } = await query;

    if (error) {
      console.error("Error fetching guide updates for assistant:", error);
      setLoading(false);
      return;
    }
    setUpdates((data as GuideUpdate[]) ?? []);
    setLoading(false);
  }, [assistantId, organizationId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useGuideUpdatesRealtime(refresh, organizationId);

  return { updates, loading, refetch: refresh };
}
